import { useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { format } from 'date-fns';
import {
  FastField,
  Field,
  FieldAttributes,
  Form,
  Formik,
  useField,
  useFormikContext,
} from 'formik';
import Checkbox from '@/trio-ui/Checkbox';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import TransparentButton from '@/trio-ui/TransparentButton';
import { Months } from '@/utils/dates';
import { buildErrorMessage, workExperienceSchema } from '@/utils/formsValidation';
import * as Styles from './WorkExperience.styles';
import TechnologiesDropdown from '../TechnologiesDropdown';
import TextEditor from '../TextEditor';
import { WorkExperienceType } from '../WorkExperience.types';

export type WorkExperienceComponentProps = Omit<WorkExperienceType, 'id'> & { id?: string } & {
  yearOptions: string[];
  isSubmitting: boolean;
  onSaveExperience: (values: any) => void;
  onCancelExperienceEdition: () => void;
};

const DISABLED_DROPDOWN_PLACEHOLDER = '---';

const getEndValue = (
  endValue?: string | number,
  currentWork?: boolean,
  formatMask = ''
): string => {
  if (currentWork) {
    return DISABLED_DROPDOWN_PLACEHOLDER;
  }

  if (!endValue || endValue === DISABLED_DROPDOWN_PLACEHOLDER) {
    return format(new Date(), formatMask);
  }

  return String(endValue);
};

const DependentEndDateField = (props: FieldAttributes<any>): JSX.Element => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [field] = useField(props);

  const { name } = props;

  useEffect(() => {
    if (name === 'endMonth') {
      const value = getEndValue(values.endMonth, values.isCurrentWork, 'MMMM');
      setFieldValue(name, value);
    }

    if (name === 'endYear') {
      const value = getEndValue(values.endYear, values.isCurrentWork, 'yyyy');
      setFieldValue(name, value);
    }
  }, [setFieldValue, name, values.endMonth, values.isCurrentWork, values.endYear]);

  return <Field {...props} {...field} />;
};

export default function WorkExperienceComponent({
  company,
  title,
  startMonth,
  startYear,
  endMonth,
  endYear,
  isCurrentWork,
  location,
  techs,
  description,
  yearOptions,
  isSubmitting,
  onSaveExperience,
  onCancelExperienceEdition,
}: WorkExperienceComponentProps): JSX.Element {
  return (
    <Styles.Box>
      <Formik
        validationSchema={workExperienceSchema}
        initialValues={{
          company,
          title,
          startMonth,
          startYear,
          endMonth,
          endYear,
          isCurrentWork,
          location,
          techs,
          description,
        }}
        onSubmit={(values) => onSaveExperience(values)}
      >
        {(form) => {
          return (
            <Form>
              <fieldset disabled={isSubmitting}>
                <Styles.Row hasMarginBottom>
                  <Styles.Column>
                    <FastField name="company">
                      {({ field }) => {
                        return (
                          <InputText
                            value={field.value}
                            name="company"
                            label="Company"
                            errorMessage={buildErrorMessage(
                              form.touched.company,
                              form.errors.company
                            )}
                            onChangeHandler={form.handleChange}
                            isLabelAboveField
                          />
                        );
                      }}
                    </FastField>
                  </Styles.Column>

                  <Styles.Column>
                    <FastField name="title">
                      {({ field }) => {
                        return (
                          <InputText
                            value={field.value}
                            name="title"
                            label="Job Title"
                            errorMessage={buildErrorMessage(form.touched.title, form.errors.title)}
                            onChangeHandler={form.handleChange}
                            isLabelAboveField
                          />
                        );
                      }}
                    </FastField>
                  </Styles.Column>
                </Styles.Row>

                <FastField name="location">
                  {({ field }) => {
                    return (
                      <div style={{ marginBottom: '16px' }}>
                        <InputText
                          value={field.value}
                          name="location"
                          label="Location"
                          errorMessage={buildErrorMessage(
                            form.touched.location,
                            form.errors.location
                          )}
                          onChangeHandler={form.handleChange}
                          isLabelAboveField
                        />
                      </div>
                    );
                  }}
                </FastField>

                <FastField name="techs">
                  {({ field }) => {
                    return (
                      <div style={{ marginBottom: '16px' }}>
                        <TechnologiesDropdown
                          label="Technologies Used"
                          name="techs"
                          isMulti
                          closeMenuOnSelect={false}
                          defaultValue={field.value}
                          onChange={form.handleChange}
                          errorMessage={buildErrorMessage(form.touched.techs, form.errors.techs)}
                        />
                      </div>
                    );
                  }}
                </FastField>

                <Styles.Row hasMarginBottom>
                  <Styles.Column>
                    <Styles.Row>
                      <Styles.DateDropdownField flatSide="right">
                        <FastField name="startMonth">
                          {({ field }) => {
                            return (
                              <DropDown
                                label="From"
                                name="startMonth"
                                isLabelAboveField
                                errorMessage={buildErrorMessage(
                                  form.touched.startMonth,
                                  form.errors.startMonth
                                )}
                                defaultValue={String(field.value)}
                                onChangeDropDown={form.handleChange}
                                options={Object.keys(Months)}
                              />
                            );
                          }}
                        </FastField>
                      </Styles.DateDropdownField>

                      <Styles.DateDropdownField flatSide="left">
                        <FastField name="startYear">
                          {({ field }) => {
                            return (
                              <DropDown
                                label=" "
                                name="startYear"
                                isLabelAboveField
                                errorMessage={buildErrorMessage(
                                  form.touched.startYear,
                                  form.errors.startYear
                                )}
                                defaultValue={String(field.value)}
                                onChangeDropDown={form.handleChange}
                                options={yearOptions}
                              />
                            );
                          }}
                        </FastField>
                      </Styles.DateDropdownField>
                    </Styles.Row>
                  </Styles.Column>

                  <Styles.Column>
                    <Styles.Row>
                      <Styles.DateDropdownField flatSide="right">
                        <DependentEndDateField name="endMonth">
                          {({ field }) => {
                            return (
                              <DropDown
                                isDisabled={form.values.isCurrentWork}
                                label="To"
                                name="endMonth"
                                isLabelAboveField
                                errorMessage={buildErrorMessage(
                                  form.touched.endMonth,
                                  form.errors.endMonth
                                )}
                                defaultValue={getEndValue(
                                  field.value,
                                  form.values.isCurrentWork,
                                  'MMMM'
                                )}
                                onChangeDropDown={form.handleChange}
                                options={Object.keys(Months)}
                              />
                            );
                          }}
                        </DependentEndDateField>
                      </Styles.DateDropdownField>

                      <Styles.DateDropdownField flatSide="left">
                        <DependentEndDateField name="endYear">
                          {({ field }) => {
                            return (
                              <DropDown
                                isDisabled={form.values.isCurrentWork}
                                label=" "
                                name="endYear"
                                isLabelAboveField
                                errorMessage={buildErrorMessage(
                                  form.touched.endYear,
                                  form.errors.endYear
                                )}
                                defaultValue={getEndValue(
                                  field.value,
                                  form.values.isCurrentWork,
                                  'yyyy'
                                )}
                                onChangeDropDown={form.handleChange}
                                options={yearOptions}
                              />
                            );
                          }}
                        </DependentEndDateField>
                      </Styles.DateDropdownField>
                    </Styles.Row>
                  </Styles.Column>
                </Styles.Row>

                <Field name="isCurrentWork">
                  {({ field }) => {
                    return (
                      <Checkbox
                        label="Currently work here"
                        checked={field.checked || field.value}
                        name="isCurrentWork"
                        onChangeHandler={form.handleChange}
                        errorMessage={buildErrorMessage(
                          form.touched.isCurrentWork,
                          form.errors.isCurrentWork
                        )}
                      />
                    );
                  }}
                </Field>

                <FastField name="description">
                  {({ field }) => {
                    return (
                      <div>
                        <Styles.TextAreaLabel>Description</Styles.TextAreaLabel>
                        <TextEditor
                          errorMessage={buildErrorMessage(
                            form.touched.description,
                            form.errors.description
                          )}
                          isDisabled={form.isSubmitting}
                          value={field.value}
                          onChangeText={(e) => {
                            return form.handleChange({
                              type: 'change',
                              target: {
                                value: e,
                                name: 'description',
                              },
                            });
                          }}
                          isEditing
                          hideButtons
                          hideEditButton
                        />
                      </div>
                    );
                  }}
                </FastField>

                <Styles.Footer>
                  <TransparentButton
                    isBlueButton
                    type="submit"
                    icon={SaveIcon}
                    disabled={isSubmitting}
                  >
                    Save
                  </TransparentButton>
                  <TransparentButton
                    icon={DeleteIcon}
                    isRedButton
                    onClick={() => {
                      onCancelExperienceEdition();
                      form.resetForm();
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </TransparentButton>
                </Styles.Footer>
              </fieldset>
            </Form>
          );
        }}
      </Formik>
    </Styles.Box>
  );
}
